import { useEffect, useState } from 'react';
import { Filter } from 'lucide-react';
import { getSchluesselDetail, type SchluesselDetailDto, type StatsAnfrage } from '../../../lib/apiStatsApi';
import ZeitreiheChart from './ZeitreiheChart';
import { formatZeitpunkt } from './format';

interface SchluesselDetailProps {
  id: number;
  anfrage: StatsAnfrage;
  darkMode: boolean;
  onEndpunktWaehlen: (route: string) => void;
}

/** Ausklapp-Detail einer Schlüssel-Zeile: Verlauf, meistgenutzte Endpunkte
 *  (mit Ø- und Max-Antwortzeit), letzte Fehler mit Status und Zeitpunkt. */
export default function SchluesselDetail({ id, anfrage, darkMode, onEndpunktWaehlen }: SchluesselDetailProps) {
  const [daten, setDaten] = useState<SchluesselDetailDto | null>(null);
  const [fehler, setFehler] = useState(false);
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  useEffect(() => {
    let aktiv = true;
    setDaten(null);
    setFehler(false);
    getSchluesselDetail(id, anfrage)
      .then(d => { if (aktiv) setDaten(d); })
      .catch(() => { if (aktiv) setFehler(true); });
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, JSON.stringify(anfrage)]);

  if (fehler) return <p className={`px-4 py-4 text-sm ${muted}`}>Detail konnte nicht geladen werden.</p>;
  if (!daten) return <p className={`px-4 py-4 text-sm ${muted}`}>Lade …</p>;

  return (
    <div className="px-4 py-4 space-y-4">
      <ZeitreiheChart punkte={daten.zeitreihe} aufloesung={daten.aufloesung} darkMode={darkMode} hoehe={180} />

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Meistgenutzte Endpunkte</h4>
          {daten.topEndpunkte.length === 0 ? (
            <p className={`text-sm ${muted}`}>Keine Aufrufe im Zeitraum</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={muted}>
                  <th className="text-left font-medium pb-1">Endpunkt</th>
                  <th className="text-right font-medium pb-1">Aufrufe</th>
                  <th className="text-right font-medium pb-1">Fehler</th>
                  <th className="text-right font-medium pb-1">Ø ms</th>
                  <th className="text-right font-medium pb-1">Max ms</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                {daten.topEndpunkte.map(t => (
                  <tr key={t.route}>
                    <td className="py-1.5 pr-2">
                      <button
                        type="button"
                        onClick={() => onEndpunktWaehlen(t.route)}
                        title="Auf diesen Endpunkt filtern"
                        className="inline-flex items-center gap-1.5 font-mono text-left hover:underline cursor-pointer"
                      >
                        <Filter className="w-3 h-3 opacity-50 shrink-0" />
                        {t.route}
                      </button>
                    </td>
                    <td className="py-1.5 text-right">{t.aufrufe}</td>
                    <td className={`py-1.5 text-right ${t.fehler > 0 ? 'text-red-500' : ''}`}>{t.fehler}</td>
                    <td className="py-1.5 text-right">{t.dauerMsSchnitt}</td>
                    <td className="py-1.5 text-right">{t.dauerMsMax}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Letzte Fehler</h4>
          {daten.letzteFehler.length === 0 ? (
            <p className={`text-sm ${muted}`}>Keine Fehler aufgezeichnet</p>
          ) : (
            <ul className={`divide-y text-sm ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
              {daten.letzteFehler.map((f, i) => (
                <li key={i} className="py-1.5 flex items-baseline gap-3">
                  <span className={`shrink-0 px-1.5 rounded text-xs font-semibold ${f.status >= 500 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {f.status}
                  </span>
                  <span className="font-mono truncate">{f.route}</span>
                  <span className={`ml-auto shrink-0 ${muted}`}>{formatZeitpunkt(f.zeitpunkt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
